'use client';

import { memo, useEffect, useRef } from 'react';
import { ArticleCardGrid } from '@/app_components/ArticleSections/ArticleCards/ArticleCardGrid';
import { ArticleCardSmall } from '@/app_components/ArticleSections/ArticleCards/ArticleCardSmall';
import { ArticleCardVertical } from '@/app_components/ArticleSections/ArticleCards/ArticleCardVertical';
import type { ArticlePreview, ArticlePreviewGrid } from '@/app_lib/types/ArticlePreview';
import { cn } from '@/app_lib/utils/utils';

function nativoCarouselFeedSlotInner({
  nativoCardId,
  fallbackArticle,
  onNativoFilledChange,
  priority,
}: {
  nativoCardId: number;
  fallbackArticle: ArticlePreviewGrid;
  onNativoFilledChange?: (filled: boolean) => void;
  priority?: boolean;
}) {
  /** Root of this slot; Nativo may rewrite DOM under or around it. */
  const slotRef = useRef<HTMLDivElement>(null);
  const reportedRef = useRef(false);
  const onFilledRef = useRef(onNativoFilledChange);
  onFilledRef.current = onNativoFilledChange;

  useEffect(() => {
    const slot = slotRef.current;
    const parent = slot?.parentElement?.parentElement;
    if (!(slot && parent && onFilledRef.current)) {
      return;
    }

    const reportFilled = () => {
      if (reportedRef.current) {
        return;
      }
      if (parent.querySelector('.ntvClickOut')) {
        reportedRef.current = true;
        onFilledRef.current?.(true);
        return;
      }
    };

    const observer = new MutationObserver(() => {
      reportFilled();
    });

    observer.observe(parent, { childList: true, subtree: true });
    reportFilled();

    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={cn('article-card-wrapper snap-start pl-4 md:pl-0 article-card')}
      ref={slotRef}
    >
      <ul className={cn('list-none m-0 p-0', `nativo-topic-${nativoCardId - 1}`)}>
        <li className='w-full'>
          <div data-nativo-carousel-fallback>
            <ArticleCardGrid
              article={fallbackArticle}
              className='snap-start min-w-0'
              priority={priority}
              source='carousel'
            />
          </div>
        </li>
      </ul>
    </div>
  );
}

/** Memoized so parent can append the shifted article when Nativo fills without re-rendering this subtree (Nativo-injected DOM). */
const NativoCarouselFeedSlotMemo = memo(nativoCarouselFeedSlotInner);

// biome-ignore lint/style/useNamingConvention: React slot component
function NativoGridFeedSlot({
  nativoCardId,
  fallbackArticle,
  priority,
  source,
}: {
  nativoCardId: number;
  fallbackArticle: ArticlePreviewGrid;
  priority?: boolean;
  source: 'section_grid' | 'carousel' | 'flood';
}) {
  return (
    <div className='flex w-full min-h-0'>
      <ul className={cn('list-none m-0 p-0 w-full block', `nativo-well-${nativoCardId}`)}>
        <li className='w-full'>
          <ArticleCardGrid
            article={fallbackArticle}
            priority={priority}
            source={source}
          />
        </li>
      </ul>
    </div>
  );
}

// biome-ignore lint/style/useNamingConvention: React slot component
function NativoSectionFeaturedFeedSlot({
  nativoSectionId,
  fallbackArticle,
  priority,
}: {
  nativoSectionId: number;
  fallbackArticle: ArticlePreview;
  priority?: boolean;
}) {
  return (
    <div className={cn('h-full w-full min-h-0', `nativo-section-${nativoSectionId - 1}`)}>
      <ArticleCardSmall
        article={fallbackArticle}
        index={3}
        moduleType='section_featured'
        priority={priority}
      />
    </div>
  );
}

// biome-ignore lint/style/useNamingConvention: React slot component
function NativoSectionCategoryFeedSlot({
  nativoSectionId,
  fallbackArticle,
}: {
  nativoSectionId: number;
  fallbackArticle: ArticlePreview;
}) {
  return (
    <div className={cn('w-full min-h-0', `nativo-section-${nativoSectionId - 1}`)}>
      <ArticleCardVertical
        article={fallbackArticle}
        className='pb-sm'
        index={3}
        moduleType='child_featured'
      />
    </div>
  );
}

export {
  NativoCarouselFeedSlotMemo as NativoCarouselFeedSlot,
  NativoGridFeedSlot,
  NativoSectionCategoryFeedSlot,
  NativoSectionFeaturedFeedSlot,
};
